import { useState, useEffect, useRef } from 'react';
import { useMeasure, useIntersectionObserver } from '@uidotdev/usehooks';

/**
 * Hook personalizado para detectar si el usuario ha leído completamente un documento
 * @param {number} maxHeight - Altura máxima del contenedor (400px por defecto)
 * @returns {object} - { hasReaded, contentRef, sentinelRef, measureRef }
 */
export const useDocumentReadTracker = (maxHeight = 400) => {
	const [hasReaded, setHasReaded] = useState(false);
	const contentRef = useRef(null);

	// Hook para medir el tamaño del contenido
	const [measureRef, { height: contentHeight }] = useMeasure();

	// Hook para detectar cuando el centinela al final del documento es visible
	const [sentinelRef, entry] = useIntersectionObserver({
		threshold: 0,
		root: null, // Usar el viewport como root en lugar del contenedor
		rootMargin: '0px', // Sin margen para una detección más precisa
	});

	useEffect(() => {
		// Si el contenido es más pequeño que el contenedor, no necesita scroll
		if (contentHeight && contentHeight <= maxHeight) {
			setHasReaded(true);
			return;
		}

		// Si el contenido es más grande, necesita que el usuario haga scroll hasta el final
		if (entry && entry.isIntersecting) {
			setHasReaded(true);
		}
	}, [contentHeight, maxHeight, entry]);

	// Reset cuando cambia el contenido
	useEffect(() => {
		setHasReaded(false);
	}, [contentHeight]);

	return {
		hasReaded,
		contentRef,
		sentinelRef,
		measureRef,
		contentHeight,
		requiresScroll: contentHeight > maxHeight,
	};
};
